import {createRefetchContainer, graphql} from 'react-relay';
import React, {useEffect, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import {FormattedMessage} from 'react-intl';
import {FlexColumn, Heading, Frame, Icon, Text, Button, FlexRow} from '@forecast-it/design-system';
import ProjectInvoicingTotals from './ProjectInvoicingTotals';
import ProjectInvoicingTableSection from './ProjectInvoicingTableSection';
import ProjectInvoicingUninvoicedItemsTableSection from './ProjectInvoicingUninvoicedItemsTableSection';
import ProjectInvoicingFixedPriceDeliverablesSection from './ProjectInvoicingFixedPriceDeliverablesSection';
import Util from '../../../../forecast-app/shared/util/util';
import ClientSelector from '../../../../components/client_selector';
import {hasPermission} from '../../../../forecast-app/shared/util/PermissionsUtil';
import UpdateProjectMutation from '../../../../mutations/update_project_mutation.modern';
import GenericEmptyState from '../../empty_states/generic_empty_state';
import {PERMISSION_TYPE} from '../../../../Permissions';
import {cloneDeep} from 'lodash';
import {MODAL_TYPE, showModal} from '../../../../forecast-app/shared/components/modals/generic_modal_conductor';
import {Route, useLocation, useRouteMatch} from 'react-router-dom';
import StandaloneEditInvoiceModalWrapper from '../../../modal/edit_invoice_modal/StandaloneEditInvoiceModalWrapper';
import {BUDGET_TYPE, FILTER_TYPE} from '../../../../constants';
import {dateFragmentPropsFromIntervalInIsoFormat, dateObjectToIsoDate} from '../../../../forecast-app/shared/util/DateUtil';
import moment from 'moment';
import CustomScrollDiv from '../../../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import {trackEvent} from '../../../../tracking/amplitude/TrackingV2';
import {useTrackPage} from '../../../../tracking/amplitude/hooks/useTrackPage';
import {updateProjectOnSuccess} from '../../../../forecast-app/project-tab/projects/project-settings/ProjectSettingsMutations';
import {filtersToObject} from '../../../../forecast-app/shared/components/filters/filter_logic';
import {FILTER_SECTIONS} from '../../../../forecast-app/shared/components/filters/dropdown_section';
import {LOCALSTORAGE_FILTERS_KEY} from '../../../../components/insights/invoicing/InvoicesPage';
import {
	isXeroInvoicingImprovementsEnabledOrOff,
	isQboInvoicingImprovementsEnabledOrOff,
} from '../../../../forecast-app/shared/util/FeatureUtil';

/*
 * T4-197 (D8): the client default tax rate is requested only with the flag on; T4-188 does the same
 * for the QuickBooks default with its own flag. The fragments default both flags to false, so every
 * operation that reaches them - the initial query and the refetch - must pass them, or the rate
 * silently goes missing.
 */
export const projectInvoicingPageQueryVariables = (projectId, startDate, endDate) => ({
	projectId,
	invoiceProjectIds: projectId ? [projectId] : null,
	...dateFragmentPropsFromIntervalInIsoFormat(startDate, endDate),
	xeroInvoicingImprovements: isXeroInvoicingImprovementsEnabledOrOff(),
	qboInvoicingImprovements: isQboInvoicingImprovementsEnabledOrOff(),
});

const ProjectInvoicingPage = ({viewer, relay: {refetch}}) => {
	useTrackPage('Project Financials Invoicing', null, {budgetType: viewer.project.budgetType});
	const {formatMessage} = useIntl();
	const {path} = useRouteMatch();
	const location = useLocation();
	const history = useHistory();
	const [hasClient, setHasClient] = useState(!!viewer.project.client);

	const [startDate, setStartDate] = useState(dateObjectToIsoDate(moment().startOf('month')));
	const [endDate, setEndDate] = useState(dateObjectToIsoDate(moment().endOf('month')));

	const isProjectInMultiProjectInvoices = useMemo(() => {
		const {edges} = viewer.invoicesV2 || {};

		if (!edges || edges.length === 0) {
			return false;
		}

		return edges.some(edge => edge?.node?.projects?.length > 1);
	}, [viewer.invoicesV2]);

	const onDatePickerDateChanged = newDates => {
		trackEvent('Uninvoiced Date Range', 'Changed', {startDate: newDates.startDate, endDate: newDates.endDate});
		setStartDate(newDates.startDate);
		setEndDate(newDates.endDate);
	};

	const doRefetch = () => {
		refetch({
			projectId: viewer.project.id,
			...dateFragmentPropsFromIntervalInIsoFormat(startDate, endDate),
			xeroInvoicingImprovements: isXeroInvoicingImprovementsEnabledOrOff(),
			qboInvoicingImprovements: isQboInvoicingImprovementsEnabledOrOff(),
		});
	};

	useEffect(doRefetch, [startDate, endDate]);

	const handleSelectClient = selected => {
		const onSuccess = res => {
			setHasClient(true);
			updateProjectOnSuccess(res);
		};
		Util.CommitMutation(
			UpdateProjectMutation,
			{
				project: viewer.project,
				clientId: selected ? (selected.value ? selected.value : selected) : null,
			},
			onSuccess
		);
	};

	const showAddClientModal = () => {
		showModal({type: MODAL_TYPE.ADD_CLIENT});
	};

	if (isProjectInMultiProjectInvoices) {
		const navigateToAllInvoicesWithFilter = () => {
			const filterObject = filtersToObject(
				[
					{
						section: FILTER_SECTIONS.INVOICES,
						filterType: FILTER_TYPE.PROJECT,
						id: viewer.project.id,
					},
				],
				{}
			);

			Util.localStorageSetItem(LOCALSTORAGE_FILTERS_KEY, JSON.stringify(filterObject));
			history.push('/finance/invoices');
		};

		return (
			<Frame width={'l'} customPadding={'600'}>
				<FlexColumn gap={'s'}>
					<FlexRow gap={'m'}>
						<Heading size={'3'}>
							<FormattedMessage id="common.page_unavailable" />
						</Heading>
						<Icon icon="blocked" size="l"></Icon>
					</FlexRow>

					<FlexColumn gap={'l'}>
						<FlexColumn gap={'xxs'}>
							<Text>
								<FormattedMessage id="project.invoice.page_unavailable.primary_text" />
							</Text>
							<Text>
								<FormattedMessage id="project.invoice.page_unavailable.secondary_text" />
							</Text>
						</FlexColumn>

						<Button emphasis={'high'} rightIcon={'arrowRight'} onPress={navigateToAllInvoicesWithFilter}>
							<FormattedMessage id="project.invoice.page_unavailable.cta" />
						</Button>
					</FlexColumn>
				</FlexColumn>
			</Frame>
		);
	}

	return (
		<>
			<CustomScrollDiv>
				{hasClient ? (
					<FlexColumn gap={'xl'}>
						<ProjectInvoicingTotals project={viewer.project} company={viewer.company} />
						<ProjectInvoicingFixedPriceDeliverablesSection project={viewer.project} company={viewer.company} />
						{viewer.project.budgetType !== BUDGET_TYPE.FIXED_PRICE &&
							viewer.project.budgetType !== BUDGET_TYPE.FIXED_PRICE_V2 &&
							viewer.project.budgetType !== BUDGET_TYPE.NON_BILLABLE && (
								<ProjectInvoicingUninvoicedItemsTableSection
									project={viewer.project}
									startDate={startDate}
									endDate={endDate}
									onChangeDateInterval={onDatePickerDateChanged}
								/>
							)}
						<ProjectInvoicingTableSection
							viewer={viewer}
							project={viewer.project}
							company={viewer.company}
							refetch={doRefetch}
							startDate={startDate}
							endDate={endDate}
						/>
					</FlexColumn>
				) : (
					<GenericEmptyState
						iconClass={'client'}
						headerText={formatMessage({id: 'empty_state.project_client.header_text'})}
						text={formatMessage({id: 'empty_state.project_client.text'})}
						extraContent={
							hasPermission(PERMISSION_TYPE.PROJECTS_UPDATE) ? (
								<ClientSelector
									canChangeClient={true}
									clients={cloneDeep(viewer.company.clients.edges)}
									showAddClientModal={showAddClientModal}
									handleSelectItem={handleSelectClient}
									showNoClient={false}
								/>
							) : null
						}
					/>
				)}
			</CustomScrollDiv>
			<Route
				path={`${path}/I-:companyInvoiceId`}
				render={compObj =>
					location.hash === '#invoicing' ? (
						<StandaloneEditInvoiceModalWrapper
							retry={refetch}
							{...compObj}
							companyInvoiceId={compObj.match.params.companyInvoiceId}
						/>
					) : null
				}
			/>
		</>
	);
};

export const ProjectInvoicingPageQuery = graphql`
	query ProjectInvoicingPage_Query(
		$projectId: ID
		$invoiceProjectIds: [ID!]
		$startYear: Int!
		$startMonth: Int!
		$startDay: Int!
		$endYear: Int!
		$endMonth: Int!
		$endDay: Int!
		$xeroInvoicingImprovements: Boolean!
		$qboInvoicingImprovements: Boolean!
	) {
		viewer {
			actualPersonId
			component(name: "project_invoicing_page")
			...ProjectInvoicingPage_viewer
				@arguments(
					projectId: $projectId
					invoiceProjectIds: $invoiceProjectIds
					startYear: $startYear
					startMonth: $startMonth
					startDay: $startDay
					endYear: $endYear
					endMonth: $endMonth
					endDay: $endDay
					xeroInvoicingImprovements: $xeroInvoicingImprovements
					qboInvoicingImprovements: $qboInvoicingImprovements
				)
		}
	}
`;

export default createRefetchContainer(
	ProjectInvoicingPage,
	{
		viewer: graphql`
			fragment ProjectInvoicingPage_viewer on Viewer
			@argumentDefinitions(
				projectId: {type: "ID"}
				invoiceProjectIds: {type: "[ID!]"}
				startYear: {type: "Int!", defaultValue: 2020}
				startMonth: {type: "Int!", defaultValue: 1}
				startDay: {type: "Int!", defaultValue: 1}
				endYear: {type: "Int!", defaultValue: 2099}
				endMonth: {type: "Int!", defaultValue: 12}
				endDay: {type: "Int!", defaultValue: 31}
				xeroInvoicingImprovements: {type: "Boolean!", defaultValue: false}
				qboInvoicingImprovements: {type: "Boolean!", defaultValue: false}
			) {
				...ProjectInvoicingTableSection_viewer
				invoicesV2(first: 100000, invoiceProjectIds: $invoiceProjectIds) {
					edges {
						node {
							id
							projects {
								id
							}
						}
					}
				}
				company {
					currency
					clients {
						edges {
							node {
								id
								name
								xeroContactId
								economicCustomerId
								quickbooksCustomerId
								sageIntacctCustomerId
							}
						}
					}
					...ProjectInvoicingTableSection_company
						@arguments(
							xeroInvoicingImprovements: $xeroInvoicingImprovements
							qboInvoicingImprovements: $qboInvoicingImprovements
						)
				}
				project(internalId: $projectId) {
					id
					client {
						id
					}
					budgetType
					...ProjectInvoicingTotals_project
					...ProjectInvoicingFixedPriceDeliverablesSection_project
					...ProjectInvoicingUninvoicedItemsTableSection_project
						@arguments(
							startYear: $startYear
							startMonth: $startMonth
							startDay: $startDay
							endYear: $endYear
							endMonth: $endMonth
							endDay: $endDay
						)
					...ProjectInvoicingTableSection_project
				}
			}
		`,
	},
	graphql`
		query ProjectInvoicingPageRefetchQuery(
			$projectId: ID
			$invoiceProjectIds: [ID!]
			$startYear: Int!
			$startMonth: Int!
			$startDay: Int!
			$endYear: Int!
			$endMonth: Int!
			$endDay: Int!
			$xeroInvoicingImprovements: Boolean!
			$qboInvoicingImprovements: Boolean!
		) {
			viewer {
				...ProjectInvoicingPage_viewer
					@arguments(
						projectId: $projectId
						invoiceProjectIds: $invoiceProjectIds
						startYear: $startYear
						startMonth: $startMonth
						startDay: $startDay
						endYear: $endYear
						endMonth: $endMonth
						endDay: $endDay
						xeroInvoicingImprovements: $xeroInvoicingImprovements
						qboInvoicingImprovements: $qboInvoicingImprovements
					)
			}
		}
	`
);
